import {
  Component,
  OnInit,
  OnDestroy,
  inject,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { State } from '../../core/state';
import { User, UserRoles } from '../../core/state/users/models';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';
import { Subscription } from 'rxjs';
import { Resource } from '../../core/state/resource/models';
import { DashboardSidebarService } from '../../services/dashboard-sidebar.service';

import { TenantService, TenantInfo } from '../../services/tenant.service';
import * as UserActions from '../../core/state/users/user.actions';

@Component({
  selector: 'black-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
  standalone: false,
})
export class HeaderComponent implements OnInit, OnDestroy {
  private store = inject<Store<State>>(Store);
  private router = inject(Router);
  public sidebarService = inject(DashboardSidebarService);
  public tenantService = inject(TenantService);

  isUserInstructor = false;
  isHidden = false;
  isLoggedIn = false;
  loggedInUser: Partial<User> | null = null;
  currentTenant: TenantInfo | null = null;
  isDashboardRoute = false;
  isAppMode = false;

  get showCourses(): boolean {
    return this.currentTenant?.settings?.features?.courses !== false;
  }

  /** Controls the mobile nav panel (app routes only) */
  showMobileNav = false;

  private subscription: Subscription = new Subscription();

  get tenantLogoUrl(): string | undefined {
    return this.currentTenant?.settings?.branding?.logo || undefined;
  }

  get isThemeToggleAvailable(): boolean {
    return this.tenantService.isThemeToggleAvailable;
  }

  get isDarkMode(): boolean {
    return this.tenantService.isDarkMode;
  }

  toggleTheme(): void {
    this.tenantService.toggleTheme();
  }

  get isDefaultTenant(): boolean {
    return this.tenantService.isDefaultTenant();
  }

  ngOnInit(): void {
    this.subscription.add(
      this.tenantService.currentTenant$.subscribe((tenant) => {
        this.currentTenant = tenant;
      })
    );

    this.subscription.add(
      this.tenantService.appMode$.subscribe((appMode) => {
        this.isAppMode = appMode;
      })
    );

    this.subscription.add(
      this.store
        .select((store) => store.users)
        .subscribe((userState) => {
          this.isLoggedIn = userState.loggedIn ?? false;
          this.loggedInUser = userState.user;

          if (
            userState.user &&
            (userState.user.role === UserRoles.INSTRUCTOR ||
              userState.user.role === UserRoles.ADMIN)
          ) {
            this.isUserInstructor = true;
          } else {
            this.isUserInstructor = false;
          }
        })
    );

    this.subscription.add(
      this.store
        .select((state) => state.config.hideHeader)
        .subscribe((hideHeader) => {
          this.isHidden = hideHeader;
        })
    );

    // Track whether we're on a dashboard route
    this.subscription.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {
          this.isDashboardRoute = event.urlAfterRedirects.startsWith('/dashboard');
        })
    );

    // Set initial value for dashboard route detection
    this.isDashboardRoute = this.router.url.startsWith('/dashboard');

    // Close mobile nav on any navigation
    this.subscription.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationStart))
        .subscribe(() => {
          this.closeMobileNav();
        })
    );
  }

  get hasIncompleteTutorProfile(): boolean {
    if (!this.isUserInstructor) return false;
    const tInfo = {
      ...(this.loggedInUser?.profile?.tutorInfo || {}),
      ...(this.loggedInUser?.pendingTutorProfileUpdates?.tutorInfo || {}),
    };
    const bio =
      this.loggedInUser?.pendingTutorProfileUpdates?.personal?.biography ||
      this.loggedInUser?.profile?.personal?.biography;
    return (
      !tInfo ||
      !tInfo.subjects?.length ||
      !tInfo.whatsAppNumber ||
      !bio ||
      bio.trim() === ''
    );
  }

  get showTutorProfile(): boolean {
    return !this.tenantService.isSoloInstructor();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  openMobileNav(): void {
    this.showMobileNav = true;
  }

  closeMobileNav(): void {
    this.showMobileNav = false;
  }

  /** On mobile: if dashboard route → toggle sidebar; else → open nav panel */
  handleMobileHamburger(): void {
    if (this.isDashboardRoute) {
      this.sidebarService.toggle();
    } else {
      this.openMobileNav();
    }
  }

  get lastName() {
    return this.loggedInUser?.name?.lastName;
  }

  get fullName() {
    if (this.loggedInUser?.name) {
      return `${this.loggedInUser.name.firstName} ${this.loggedInUser.name.lastName}`;
    }
    return '';
  }

  get avatarInitial() {
    if (this.loggedInUser?.name?.lastName) {
      return this.loggedInUser.name.lastName.charAt(0);
    }
    if (this.loggedInUser?.name?.firstName) {
      return this.loggedInUser.name.firstName.charAt(0);
    }
    return 'U';
  }

  getAvatarUrl(avatar: Resource | undefined): string | null {
    return avatar?.path || null;
  }

  logout() {
    this.store.dispatch(UserActions.logout({ redirect: true }));
  }
}
